// Drawing the Living Khipu, on one full-viewport canvas under the page's
// copy. Every strand cuts a gap in whatever it crosses: the gap is erased
// (destination-out), so the page shows through. What runs behind the hero's
// copy fades (the veil), so the words stay easy to read.
import { knotGeometry } from "../lib/knot";
import { Cord, Dye, KnotRun, N, frameAt, pointAt } from "./cords";
import { Rect } from "./scenes";

export type Palette = Record<Dye, string> & { ground: string; muted: string };

/** A line from a cord to the bubble hung off it (the Friends scene). */
export type Tie = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  alpha: number;
};

export const gapOf = (w: number) => Math.max(1.1, w * 0.2);

export const traceCord = (ctx: CanvasRenderingContext2D, c: Cord) => {
  const { x: X, y: Y } = c;
  ctx.beginPath();
  if (c.hitch) {
    // A short loop over the cord it hangs from.
    ctx.moveTo(X[0], Y[0] - c.w * 1.15);
    ctx.lineTo(X[0], Y[0]);
  } else ctx.moveTo(X[0], Y[0]);
  for (let k = 1; k < N - 1; k++)
    ctx.quadraticCurveTo(
      X[k],
      Y[k],
      (X[k] + X[k + 1]) / 2,
      (Y[k] + Y[k + 1]) / 2,
    );
  ctx.lineTo(X[N - 1], Y[N - 1]);
};

// Stroke the current path twice: its casing (erased), then the strand
// itself.
export const strokeCased = (
  ctx: CanvasRenderingContext2D,
  w: number,
  color: string,
  alpha: number,
) => {
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "destination-out";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = w + 2 * gapOf(w);
  ctx.stroke();
  ctx.globalCompositeOperation = "source-over";
  ctx.strokeStyle = color;
  ctx.lineWidth = w;
  ctx.stroke();
};

/** The length of a cubic Bézier given as its 8 coordinates. */
export const bezLen = (m: number[]) => {
  let len = 0;
  let px = m[0];
  let py = m[1];
  for (let q = 1; q <= 12; q++) {
    const t = q / 12;
    const u = 1 - t;
    const x =
      u * u * u * m[0] +
      3 * u * u * t * m[2] +
      3 * u * t * t * m[4] +
      t * t * t * m[6];
    const y =
      u * u * u * m[1] +
      3 * u * u * t * m[3] +
      3 * u * t * t * m[5] +
      t * t * t * m[7];
    len += Math.hypot(x - px, y - py);
    px = x;
    py = y;
  }
  return len;
};

// A long knot centred on (x, y), turned to the cord's angle, `k` times the
// size of the logo's.
export const drawKnot = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  ang: number,
  color: string,
  kn: KnotRun,
  alpha: number,
) => {
  const { st } = kn;
  if (st.p <= 0.01) return;
  const pitch = 11 + (8.1 - 11) * st.c;
  // A growing knot keeps its first turns where they are and winds new ones
  // on at the end.
  const cx = kn.grow ? ((kn.n - Math.max(st.p, 1)) * pitch) / 2 : 0;
  const g = knotGeometry({ n: kn.n, p: st.p, c: st.c, cx, cy: 0 });
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  ctx.scale(kn.kk, kn.kk);
  ctx.lineCap = "round";
  ctx.globalAlpha = alpha;
  for (const t of g.turns) {
    if (t.f <= 0.001) continue;
    const { m } = t;
    ctx.beginPath();
    ctx.moveTo(m[0], m[1]);
    ctx.bezierCurveTo(m[2], m[3], m[4], m[5], m[6], m[7]);
    if (t.f < 0.999) {
      const len = bezLen(m);
      ctx.setLineDash([t.f * len, len * 2]);
    } else ctx.setLineDash([]);
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = g.cw;
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = g.tw;
    ctx.stroke();
  }
  ctx.restore();
};

const knotOn = (
  ctx: CanvasRenderingContext2D,
  c: Cord,
  kn: KnotRun,
  color: string,
  alpha: number,
) => {
  const f = frameAt(c, kn.s, Math.max(6, kn.spanPx * 0.5));
  drawKnot(ctx, f.x, f.y, f.ang, color, kn, alpha);
};

// Two strands plied round the cord's path, drawn back to front in short
// pieces so they pass over and under each other.
export const drawPly = (
  ctx: CanvasRenderingContext2D,
  c: Cord,
  colA: string,
  colB: string,
  alpha: number,
  time: number,
) => {
  const n = Math.max(8, Math.ceil(c.total / 3));
  const A = c.w * 0.48;
  const per = c.w * 6.4;
  const sw = c.w * 0.94;
  const pts: number[][] = [];
  for (let q = 0; q <= n; q++) {
    const s = q / n;
    const p = pointAt(c, s);
    const p2 = pointAt(c, Math.min(1, s + 1 / n));
    const p0 = pointAt(c, Math.max(0, s - 1 / n));
    let tx = p2.x - p0.x;
    let ty = p2.y - p0.y;
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl;
    ty /= tl;
    const ph = ((s * c.total) / per) * Math.PI * 2 - time * 2.4;
    const o = Math.sin(ph) * A;
    pts.push([
      p.x - ty * o,
      p.y + tx * o,
      p.x + ty * o,
      p.y - tx * o,
      Math.cos(ph),
    ]);
  }
  const segs: [number, number, number, number, number, string][] = [];
  for (let q = 0; q < n; q++) {
    const a = pts[q];
    const b = pts[q + 1];
    const z = (a[4] + b[4]) / 2;
    segs.push([a[0], a[1], b[0], b[1], z, colA]);
    segs.push([a[2], a[3], b[2], b[3], -z, colB]);
  }
  segs.sort((u, v) => u[4] - v[4]);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.lineCap = "butt";
  const gw = sw + 2 * gapOf(sw);
  for (const [x0, y0, x1, y1, , col] of segs) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const l = Math.hypot(dx, dy) || 1;
    const ex = (dx / l) * 0.6;
    const ey = (dy / l) * 0.6;
    ctx.beginPath();
    ctx.moveTo(x0 - ex, y0 - ey);
    ctx.lineTo(x1 + ex, y1 + ey);
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = gw;
    ctx.strokeStyle = "#000";
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
    ctx.lineWidth = sw;
    ctx.strokeStyle = col;
    ctx.stroke();
  }
  ctx.restore();
};

// Beads of light running down an import cord: holes punched through it.
// Beads running along a cord: something on its way along it. Cut out of the
// cord, so they're holes in it whatever its colour.
export const drawBeads = (
  ctx: CanvasRenderingContext2D,
  c: Cord,
  time: number,
  alpha: number,
) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "destination-out";
  for (let q = 0; q < 4; q++) {
    const p = pointAt(c, (time * 0.2 + q / 4 + c.i * 0.13) % 1);
    ctx.beginPath();
    ctx.arc(p.x, p.y, c.w * 0.24, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
};

export type Frame = {
  back: CanvasRenderingContext2D;
  W: number;
  H: number;
  dpr: number;
  cords: Cord[];
  palette: Palette;
  ties: Tie[];
  /** The hero's copy, in the viewport: what runs behind it fades. */
  veil: Rect | null;
  time: number;
};

// How much of a cord still shows behind the hero's copy.
const TRACE = 0.14;
const EASE_STRIPS = 10;

export const draw = (f: Frame) => {
  const { back: bx, W, H, dpr, cords, palette } = f;
  bx.setTransform(dpr, 0, 0, dpr, 0, 0);
  bx.clearRect(0, 0, W, H);
  bx.lineCap = "round";
  bx.lineJoin = "round";
  const later: [Cord, KnotRun][] = [];
  for (const c of cords) {
    if (c.alpha < 0.01) continue;
    const col = palette[c.dye];
    if (c.ply > 0.01)
      drawPly(bx, c, col, palette.olive, c.alpha * c.ply, f.time);
    if (c.ply < 0.99) {
      traceCord(bx, c);
      strokeCased(bx, c.w, col, c.alpha * (1 - c.ply));
    }
    if (c.beads > 0.01) drawBeads(bx, c, f.time, c.alpha * c.beads);
    for (const kn of c.knots.values()) {
      if (kn.top) later.push([c, kn]);
      else knotOn(bx, c, kn, col, c.alpha);
    }
  }
  for (const [c, kn] of later) knotOn(bx, c, kn, palette[c.dye], c.alpha);
  bx.globalAlpha = 1;
  bx.globalCompositeOperation = "source-over";
  bx.setLineDash([]);
  bx.lineWidth = 1.5;
  bx.strokeStyle = palette.muted;
  for (const t of f.ties) {
    bx.globalAlpha = 0.55 * t.alpha;
    bx.beginPath();
    bx.moveTo(t.x0, t.y0);
    bx.lineTo(t.x1, t.y1);
    bx.stroke();
  }
  bx.globalAlpha = 1;

  // Behind the hero's copy the cords fade to a trace, and come back past
  // its right edge.
  if (f.veil) {
    const v = f.veil;
    // On whole pixels, so the strips meet without a seam.
    const x1 = Math.round(v.x + v.w - 20);
    bx.save();
    bx.globalCompositeOperation = "destination-out";
    bx.fillStyle = "#000";
    bx.globalAlpha = 1 - TRACE;
    bx.fillRect(v.x, v.y, x1 - v.x, v.h);
    // Then easing off over the next 100px, in strips too fine to see.
    for (let q = 0; q < EASE_STRIPS; q++) {
      bx.globalAlpha = (1 - TRACE) * (1 - (q + 0.5) / EASE_STRIPS);
      bx.fillRect(x1 + q * 10, v.y, 10, v.h);
    }
    bx.restore();
  }
};
